/**
 * CryptoPayTab — locked-network crypto-direct payment UI.
 *
 * The token + network are fixed at payment-link creation; this component
 * just renders the QR + deposit address + status polling for the address
 * passed in via the `defaultAddress` prop (i.e. `session.cryptoDefault`).
 *
 * No network selector. No quote/lock dance. The customer's commitment to
 * the crypto rail (allocating a per-order BitGo address) happens upstream
 * in PaymentMethodChooser → POST /crypto/commit; this component is purely
 * a renderer for the resolved cryptoDefault.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import cryptoPaymentService from '../services/cryptoPaymentService.js';
import './CryptoPayTab.css';

// Adaptive polling intervals
// ---------------------------
// Goal: minimize confirmation latency without overwhelming the API.
// Backend rate limit: 100 req / 60s per IP (THROTTLE_LIMIT), so 2s is safe.
//
// Phase 1 (waiting for deposit detection): 3s — typical BitGo webhook
// arrival is within 10-60s of on-chain tx; 3s polling makes the UI feel
// "live" without burning requests.
//
// Phase 2 (deposit detected / partial / confirming): 2s — user is actively
// watching the screen; show confirmation count climbing in real time.
//
// Phase 3 (after 2 min idle with no deposit): 10s — fallback for users
// who walk away. Saves bandwidth.
//
// When tab is hidden (visibilityState !== 'visible'), polling pauses
// entirely and resumes on focus.
const POLL_INITIAL_MS = 3_000;
const POLL_ACTIVE_MS = 2_000;
const POLL_IDLE_MS = 10_000;
const POLL_IDLE_AFTER_MS = 120_000;

const TERMINAL_STATUSES = new Set([
  'COMPLETED',
  'FAILED',
  'EXPIRED',
  'CANCELLED',
  'REFUNDED',
]);

const NETWORK_WARNINGS = {
  ethereum: 'Send only ERC-20 tokens on Ethereum Mainnet.',
  polygon: 'Send only tokens on Polygon (not Ethereum!).',
  tron: 'Send only TRC-20 tokens on the TRON network.',
  bsc: 'Send only BEP-20 tokens on BNB Smart Chain.',
  arbitrum: 'Send only tokens on Arbitrum One.',
  optimism: 'Send only tokens on Optimism.',
  base: 'Send only tokens on Base.',
  solana: 'Send only SPL tokens on Solana.',
  bitcoin: 'Send BTC on the Bitcoin network only.',
};

const EXPLORER_TX_URL = {
  ethereum: (hash) => `https://etherscan.io/tx/${hash}`,
  polygon: (hash) => `https://polygonscan.com/tx/${hash}`,
  tron: (hash) => `https://tronscan.org/#/transaction/${hash}`,
  bsc: (hash) => `https://bscscan.com/tx/${hash}`,
  arbitrum: (hash) => `https://arbiscan.io/tx/${hash}`,
  optimism: (hash) => `https://optimistic.etherscan.io/tx/${hash}`,
  base: (hash) => `https://basescan.org/tx/${hash}`,
  solana: (hash) => `https://solscan.io/tx/${hash}`,
  bitcoin: (hash) => `https://www.blockchain.com/btc/tx/${hash}`,
};

const shortHash = (hash) =>
  hash && hash.length > 14 ? `${hash.slice(0, 8)}…${hash.slice(-6)}` : hash;

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', AUD: 'A$', CAD: 'C$',
  JPY: '¥', CHF: 'CHF', SGD: 'S$', HKD: 'HK$', AED: 'د.إ',
  THB: '฿', PHP: '₱', MYR: 'RM', KRW: '₩', BRL: 'R$', MXN: 'MX$',
  IDR: 'Rp', VND: '₫', NZD: 'NZ$',
};

const formatFiat = (amount, currency) => {
  const symbol = CURRENCY_SYMBOLS[currency] || '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${symbol}${amount}`;
  return `${symbol}${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatCrypto = (amount, decimals = 8) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  const shown = Math.min(decimals, 8);
  const fixed = n.toFixed(shown);
  const trimmed = fixed.replace(/0+$/, '').replace(/\.$/, '');
  if (!trimmed.includes('.')) return `${trimmed}.00`;
  const [intPart, fracPart] = trimmed.split('.');
  return fracPart.length < 2 ? `${intPart}.${fracPart.padEnd(2, '0')}` : trimmed;
};

export default function CryptoPayTab({
  token,
  session,
  defaultAddress = null,
  onStatusChange,
}) {
  const [status, setStatus] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(false);

  const statusTimerRef = useRef(null);
  const statusAbortRef = useRef(null);
  const statusBackoffRef = useRef(POLL_INITIAL_MS);
  const pollStartedAtRef = useRef(Date.now());
  const lastStatusRef = useRef(null);

  // The address comes from props (session.cryptoDefault). We don't manage
  // it as state — if defaultAddress is missing the UI shows an error.
  const address = defaultAddress;

  // Pick the next polling delay based on current state.
  // - Tab hidden → null (caller pauses polling, resumed by visibility handler)
  // - Status indicates activity (PARTIAL / confirmations > 0 / txHash) → POLL_ACTIVE_MS (2s)
  // - Within 2 min of start with no activity → POLL_INITIAL_MS (3s)
  // - After 2 min with no activity → POLL_IDLE_MS (10s)
  const pickNextDelay = useCallback((latestStatus) => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return null;
    }
    const filled = Number(latestStatus?.filledAmount) || 0;
    const confs = latestStatus?.confirmations ?? 0;
    const hasActivity =
      latestStatus?.status === 'PARTIAL' ||
      filled > 0 ||
      confs > 0 ||
      (latestStatus?.txHashes?.length ?? 0) > 0;
    if (hasActivity) return POLL_ACTIVE_MS;
    if (Date.now() - pollStartedAtRef.current > POLL_IDLE_AFTER_MS) {
      return POLL_IDLE_MS;
    }
    return POLL_INITIAL_MS;
  }, []);

  // Status poll — self-rescheduling setTimeout with adaptive delay,
  // exponential backoff on 429/network errors, AbortController on unmount,
  // clean stop on terminal status, paused on tab hidden.
  const fetchStatus = useCallback(async () => {
    if (statusAbortRef.current) statusAbortRef.current.abort();
    const ctrl = new AbortController();
    statusAbortRef.current = ctrl;

    let stop = false;
    let nextDelay;

    try {
      const res = await cryptoPaymentService.getStatus(token, {
        signal: ctrl.signal,
      });
      if (ctrl.signal.aborted) return;
      setStatus(res);
      lastStatusRef.current = res;
      statusBackoffRef.current = POLL_INITIAL_MS;
      nextDelay = pickNextDelay(res);
      if (res?.status && onStatusChange) onStatusChange(res);
      if (res?.status && TERMINAL_STATUSES.has(res.status)) stop = true;
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const code = err?.status || err?.response?.status;
      if (code === 429 || err?.type === 'network') {
        statusBackoffRef.current = Math.min(60_000, statusBackoffRef.current * 2);
        nextDelay = statusBackoffRef.current;
      } else {
        nextDelay = pickNextDelay(lastStatusRef.current);
      }
    } finally {
      if (statusAbortRef.current === ctrl) statusAbortRef.current = null;
    }

    if (!stop && !ctrl.signal.aborted && nextDelay != null) {
      statusTimerRef.current = setTimeout(fetchStatus, nextDelay);
    }
  }, [token, onStatusChange, pickNextDelay]);

  useEffect(() => {
    if (!address?.depositAddress) return undefined;
    statusBackoffRef.current = POLL_INITIAL_MS;
    pollStartedAtRef.current = Date.now();
    lastStatusRef.current = null;
    fetchStatus();

    // Resume polling immediately when tab regains focus
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !statusTimerRef.current) {
        fetchStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
      if (statusAbortRef.current) statusAbortRef.current.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [address?.depositAddress, fetchStatus]);

  const copyAddress = useCallback(() => {
    if (!address?.depositAddress) return;
    navigator.clipboard
      .writeText(address.depositAddress)
      .then(() => {
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 1500);
      })
      .catch(() => {});
  }, [address]);

  if (!address?.depositAddress) {
    return (
      <div className="crypto-tab-state crypto-tab-error">
        <p>Crypto payment unavailable for this link.</p>
        <p style={{ fontSize: 12, color: '#9ca3af' }}>
          Please contact the merchant for a new payment link.
        </p>
      </div>
    );
  }

  const expectedAmount = address.expectedAmount;
  const expectedSymbol = address.tokenSymbol;
  const expectedNetworkName = address.networkDisplayName || address.network;

  const filled = status?.filledAmount ?? '0';
  const remaining = status?.remainingAmount ?? expectedAmount ?? '0';
  const isPartial = status?.status === 'PARTIAL';
  const isCompleted = status?.status === 'COMPLETED';

  const filledN = Number(filled) || 0;
  const expectedN = Number(expectedAmount) || 0;
  const fillPct = expectedN > 0
    ? Math.min(100, Math.max(0, (filledN / expectedN) * 100))
    : 0;
  const detected = filledN > 0 || (status?.confirmations ?? 0) > 0;
  const minConfs = address.minConfirmations || 0;
  const confs = status?.confirmations ?? 0;
  const confPct = minConfs > 0
    ? Math.min(100, Math.max(0, (confs / minConfs) * 100))
    : 0;
  const hash = status?.txHashes?.[0];
  const explorer = hash && EXPLORER_TX_URL[address.network]?.(hash);

  let pill;
  if (isCompleted) pill = { cls: 'done', label: 'Payment received' };
  else if (isPartial) pill = { cls: 'partial', label: 'Partial payment received' };
  else if (detected) pill = { cls: 'confirming', label: 'Detected — confirming on chain' };
  else pill = { cls: 'pending', label: 'Waiting for your transfer' };

  return (
    <div className="crypto-tab">
      <div className="crypto-tab-amount">
        <span className="crypto-tab-label">Order Amount</span>
        <span className="crypto-tab-amount-value">
          {formatFiat(session.amount, session.currency)}
        </span>
      </div>

      <div className="crypto-tab-section">
        <div className="crypto-tab-section-title">Network</div>
        <div className="crypto-tab-locked-network">
          <span className="crypto-tab-locked-symbol">{expectedSymbol}</span>
          <span className="crypto-tab-locked-sep">·</span>
          <span className="crypto-tab-locked-network-name">
            {expectedNetworkName}
          </span>
        </div>
      </div>

      <div className="crypto-tab-address-panel">
        <div className="crypto-tab-expected">
          <span className="crypto-tab-expected-label">Send exactly</span>
          <span className="crypto-tab-expected-value">
            {formatCrypto(expectedAmount, address.decimals)} {expectedSymbol}
          </span>
          <span className="crypto-tab-expected-network">
            on {expectedNetworkName}
          </span>
        </div>

        <div className="crypto-tab-qr">
          <QRCodeCanvas
            value={address.qrPayload || address.depositAddress}
            size={140}
          />
        </div>

        <div className="crypto-tab-address">
          <div className="crypto-tab-address-label">Deposit address</div>
          <div className="crypto-tab-address-box">
            <code>{address.depositAddress}</code>
            <button type="button" onClick={copyAddress}>
              {copyFeedback ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        {NETWORK_WARNINGS[address.network] && (
          <div className="crypto-tab-warning">
            ⚠️ {NETWORK_WARNINGS[address.network]}
          </div>
        )}

        <div className="crypto-tab-progress">
          <div className={`crypto-tab-status-pill crypto-tab-status-${pill.cls}`}>
            <span className="crypto-tab-status-dot" />
            {pill.label}
          </div>

          <div className="crypto-tab-progress-row">
            <span className="crypto-tab-progress-label">Received</span>
            <strong>
              {formatCrypto(filled, address.decimals)} /{' '}
              {formatCrypto(expectedAmount, address.decimals)} {expectedSymbol}
            </strong>
          </div>
          <div className="crypto-tab-progress-bar">
            <div
              className="crypto-tab-progress-fill"
              style={{ width: `${fillPct}%` }}
            />
          </div>

          {minConfs > 0 && detected && !isCompleted && (
            <>
              <div className="crypto-tab-progress-row">
                <span className="crypto-tab-progress-label">
                  Network confirmations
                </span>
                <strong>{confs} / {minConfs}</strong>
              </div>
              <div className="crypto-tab-progress-bar">
                <div
                  className="crypto-tab-progress-fill confirming"
                  style={{ width: `${confPct}%` }}
                />
              </div>
            </>
          )}

          {isPartial && (
            <div className="crypto-tab-topup">
              Send <strong>{formatCrypto(remaining, address.decimals)} {expectedSymbol}</strong>{' '}
              more to the same address to complete this order.
            </div>
          )}

          {isCompleted && (
            <div className="crypto-tab-done">
              Order completed. You can safely close this page.
            </div>
          )}

          {!detected && !isCompleted && (
            <div className="crypto-tab-waiting">
              <div className="crypto-tab-waiting-dots">
                <span /><span /><span />
              </div>
              We auto-detect your deposit within ~30 seconds of broadcast.
            </div>
          )}

          {hash && (
            <div className="crypto-tab-tx-row">
              <span className="crypto-tab-progress-label">Transaction</span>
              {explorer ? (
                <a
                  href={explorer}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="crypto-tab-tx-link"
                >
                  {shortHash(hash)} ↗
                </a>
              ) : (
                <code className="crypto-tab-tx-hash">{shortHash(hash)}</code>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
